import { useState, useEffect, useRef } from 'react';
import { useWizardNavigation } from './use-wizard';
import { clubApi } from '@repo/api-client';
import { useSearchParams } from 'react-router';
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  MessageSquare,
  Calendar,
  MapPin,
  ExternalLink,
  ChevronDown,
} from 'lucide-react';
import { DateFormat, formatDate } from '@/data/format/format-date';
import type { CreatePublicRegistration } from '@repo/shared-types';

interface WhatsAppPhoneMockupProps {
  clubName: string;
  tournamentName: string;
  startDate: string;
  endDate: string;
}

function WhatsAppPhoneMockup({
  clubName,
  tournamentName,
  startDate,
  endDate,
}: WhatsAppPhoneMockupProps) {
  const dateText =
    startDate && endDate
      ? `${formatDate(startDate, DateFormat.DD_MM)} - ${formatDate(endDate, DateFormat.DD_MM)}`
      : '–';

  return (
    <div className="relative flex flex-col items-center flex-1 px-8">
      <div className="relative rounded-3xl border-4 border-gray-300 bg-gray-100 p-2 shadow-lg w-full flex flex-1">
        <div className="rounded-2xl bg-white overflow-hidden border border-gray-200 flex flex-col flex-1">
          <div className="bg-[#25D366] px-3 py-2 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-white shrink-0" />
            <span className="text-white font-semibold text-sm truncate">Playpadel</span>
          </div>
          <div className="p-3 bg-[#ECE5DD] flex-1 flex flex-col">
            {/* Burbuja del mensaje (estilo light) */}
            <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 text-left max-w-[95%] shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <p className="text-gray-800 text-xs leading-relaxed">
                  Hola, recibimos tu solicitud de inscripción al torneo {tournamentName}!
                </p>
                <button
                  type="button"
                  className="shrink-0 text-gray-400 p-0.5 rounded hover:bg-gray-200"
                  aria-label="Opciones"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-2 space-y-1.5 text-gray-800 text-xs">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500 shrink-0" />
                  <span>Fecha: {dateText}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500 shrink-0" />
                  <span>Lugar: {clubName}</span>
                </div>
              </div>
              <div className="mt-2 flex items-end justify-between gap-2">
                <p className="text-gray-800 text-xs">Necesitamos que confirmes tu inscripción!</p>
                <span className="text-gray-400 text-[10px] shrink-0">1:58 PM</span>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-center gap-2 rounded-xl bg-gray-600 py-2.5 px-4 text-white text-xs font-medium">
                  <ExternalLink className="w-4 h-4 shrink-0" />
                  <span>Confirmar inscripción</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface SubmissionSummary {
  nombre: string;
  companero: string;
  telefono: string;
  categoria: string;
  categoryName: string;
  horarios: string[];
}

function StepConfirm() {
  const {
    searchParams,
    goBack,
    navigateToStep,
    tournament,
    hasScheduleStep,
    minRequiredInterests,
  } =
    useWizardNavigation();

  const [, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [summary, setSummary] = useState<SubmissionSummary | null>(null);
  const submitted = useRef(false);

  const nombre = searchParams.get('nombre') || '';
  const telefono = searchParams.get('telefono') || '';
  const companero = searchParams.get('companero') || '';
  const categoria = searchParams.get('categoria') || '';
  const horarios = searchParams.get('horarios')?.split(',').filter(Boolean) || [];

  const isInscriptionOnly = tournament?.mode === 'INSCRIPTION_ONLY';
  const isWaitingList = !hasScheduleStep && !isInscriptionOnly;

  const submitRegistration = async () => {
    setStatus('loading');

    // Save summary before submitting (params will be cleared on success)
    const categoryName =
      tournament.categories.find((c: any) => c.id.toString() === categoria)?.name || '';
    setSummary({ nombre, companero, telefono, categoria, categoryName, horarios });

    try {
      const data: CreatePublicRegistration = {
        playerOne: nombre,
        playerTwo: companero,
        phone: telefono,
        category: categoria,
        timeInterests: horarios,
      };
      await clubApi.createPublicRegistration(tournament.id, data);
      setStatus('success');
      // Clear query params so a reload won't re-submit
      setSearchParams({}, { replace: true });
    } catch (err: any) {
      setStatus('error');
      const msg = err?.init?.errors
        ? Object.values(err.init.errors as Record<string, { message: string }>)
            .map((e) => e.message)
            .join('. ')
        : err?.data || err?.message || 'Ocurrió un error al enviar la inscripción.';
      setErrorMessage(msg);
    }
  };

  useEffect(() => {
    if (submitted.current) return;

    // If no params at all (e.g. page reload after success), show success screen
    if (!nombre && !telefono && !companero && !categoria) {
      submitted.current = true;
      setStatus('success');
      return;
    }

    // Validate required fields — redirect to the first incomplete step
    if (!nombre) {
      navigateToStep('nombre');
      return;
    }
    if (!telefono) {
      navigateToStep('telefono');
      return;
    }
    if (!companero) {
      navigateToStep('companero');
      return;
    }
    if (!categoria) {
      navigateToStep('categoria');
      return;
    }
    if (hasScheduleStep && horarios.length < minRequiredInterests) {
      navigateToStep('horarios');
      return;
    }

    submitted.current = true;
    submitRegistration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    navigateToStep('nombre', {
      nombre: '',
      telefono: '',
      companero: '',
      categoria: '',
      horarios: '',
    });
  };

  // ─── Loading ───────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-6 py-12">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
        <div className="text-center">
          <p className="text-xl font-semibold text-gray-700">Confirmando inscripción...</p>
          <p className="text-gray-400 mt-1">Esto puede tomar unos segundos</p>
        </div>
      </div>
    );
  }

  // ─── Error ─────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex flex-col items-center justify-center gap-6 py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Hubo un problema</h2>
            <p className="text-gray-500 max-w-sm">{errorMessage}</p>
          </div>
        </div>
        <div className="py-4 space-y-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleRetry}
            className="w-full bg-primary text-white py-4 rounded-xl text-lg font-semibold hover:bg-primary/90 active:scale-[0.98] transition-all"
          >
            Reintentar
          </button>
          <button
            type="button"
            onClick={goBack}
            className="w-full flex items-center justify-center gap-2 text-gray-400 hover:text-gray-600 py-3 text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al paso anterior
          </button>
        </div>
      </div>
    );
  }

  // ─── Success ───────────────────────────────────────────────
  return (
    <div className="flex flex-col items-center h-full overflow-y-auto py-8 px-2">
      <div className="flex flex-col items-center gap-4 text-center max-w-md flex-1">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
          Inscripción pendiente de confirmación
        </h2>

        {isWaitingList && (
          <p className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Tu inscripción quedó en lista de espera. Nos comunicaremos contigo si se libera un
            lugar. De todos modos, confirmá por WhatsApp para que quede registrada.
          </p>
        )}

        <p className="text-sm text-gray-500">
          En unos minutos recibirás un mensaje de WhatsApp. Hacé click en el botón &quot;Confirmar
          inscripción&quot; para completar tu inscripción.
        </p>

        <div className="pt-2 pb-8 flex-1 flex flex-col">
          <WhatsAppPhoneMockup
            clubName={tournament?.club?.name ?? 'Club'}
            tournamentName={tournament?.name ?? 'Torneo'}
            startDate={tournament?.startDate ?? ''}
            endDate={tournament?.endDate ?? ''}
          />
        </div>

        {summary && (
          <div className="w-full mt-4 bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Jugador 1</span>
              <span className="font-medium text-gray-800">{summary.nombre}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Jugador 2</span>
              <span className="font-medium text-gray-800">{summary.companero}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Teléfono</span>
              <span className="font-medium text-gray-800">{summary.telefono}</span>
            </div>
            {summary.categoryName && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Categoría</span>
                <span className="font-medium text-gray-800">{summary.categoryName}</span>
              </div>
            )}
            {summary.horarios.length > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Horarios</span>
                <span className="font-medium text-gray-800">
                  {summary.horarios.length} seleccionado
                  {summary.horarios.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const route = {
  path: 'confirmacion',
  element: <StepConfirm />,
};
